import { useNavigation } from '@react-navigation/native';
import Moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Animated, Easing,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import HappyDetailsGraphWeb, { happyScoreMetrics } from '../../graphs/HappyDetailsGraphWeb';
import DateComponent from '../DateComponent';
import HappyScoreIndicator from '../HappyScoreIndicator';
import SectionHeader from '../SectionHeader';
import TopSectionInsights from '../TopSectionInsights';
import HomeGraphLineSelector from './HomeGraphLineSelector';
import TileSelector, { TileDataItem } from './TileSelector';
import getClientDetails from '../../requests/HappyScore/GetClientDetails';
import { DateRange, updateDateRange } from '../../redux/dateRangeSlice';
import { updateDateComparisonRange } from '../../redux/dateComparisonRangeSlice';
import { getAverageFromGraphData, screenMaxWidth } from '../TopSection';
import { HappyScoreCategory, HappyScoreCategoryTitles } from '../../constants/Types';
import GetMetricHappyScoreData from '../../requests/Sensors/GetMetricHappyScoreData';

const defaultAnimationDuration: number = 300;

const animation = (
  animatedValue,
  toValue,
  duration = defaultAnimationDuration,
): Animated.CompositeAnimation => Animated.timing(animatedValue, {
  duration,
  useNativeDriver: true,
  easing: Easing.ease,
  toValue,
});

const linkedScreens = [
  'InternalEnvironment',
  'ExternalEnvironment',
  'IT',
  'Energy',
  'BuildingOccupancy',
  'Amenities',
  'Ergonomics',
  'OccupantSatisfaction',
];

const HomeScreenTopSection = (props) => {
  const [tileData, setTileData] = useState<TileDataItem[]>([
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 0,
      lineShown: true,
      screen: HappyScoreCategory.HAPPY_SCORE,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 1,
      lineShown: false,
      screen: HappyScoreCategory.INTERNAL_ENVIRONMENT,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 2,
      lineShown: false,
      screen: HappyScoreCategory.EXTERNAL_ENVIRONMENT,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 3,
      lineShown: false,
      screen: HappyScoreCategory.IT,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 4,
      lineShown: false,
      screen: HappyScoreCategory.ENERGY,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 5,
      lineShown: false,
      screen: HappyScoreCategory.OCCUPANCY,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 6,
      lineShown: false,
      screen: HappyScoreCategory.AMENITIES,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 7,
      lineShown: false,
      screen: HappyScoreCategory.ERGONOMICS,
    },
    {
      score: null,
      scoreCompared: null,
      average: null,
      todayScore: null,
      yesterdayScore: null,
      description: '',
      index: 8,
      lineShown: false,
      screen: HappyScoreCategory.SATISFACTION,
    },
  ]);

  const [graphData, setGraphData] = useState(tileData.map(() => []));
  const [graphCompareData, setGraphCompareData] = useState(tileData.map(() => []));
  const [topSectionDefaultShown, setTopSectionDefaultShown] = useState(true);
  const [defaultContentShown, setDefaultContentShown] = useState(topSectionDefaultShown);
  const [selectedTileIndex, setSelectedTileIndex] = useState(0);
  const [happyScoreAnimatedValue] = useState(new Animated.Value(0));
  const [happyScoreComparedAnimatedValue] = useState(new Animated.Value(0));
  const [topSectionHeight] = useState(new Animated.Value(327));
  const [happyScoreIndicatorSize] = useState(new Animated.Value(1));
  const [happyScoreIndicatorOffset] = useState(new Animated.Value(-60));
  const [topSectionMarginStart] = useState(new Animated.Value(500));
  const [sectionHeaderPaddingStart] = useState(new Animated.Value(172));
  const [graphContainerHeight] = useState(new Animated.Value(0));
  const [graphContainerMarginTop] = useState(new Animated.Value(0));
  const [graphContainerOpacity] = useState(new Animated.Value(0));
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const compareRange: DateRange = useSelector((state:
    { dateComparisonRange: DateRange }) => (state.dateComparisonRange));
  const dispatch = useDispatch();
  const setRange = (dates: DateRange) => dispatch(updateDateRange(dates));
  const setCompareRange = (dates: DateRange) => dispatch(updateDateComparisonRange(dates));

  const animateHappyScoreIndicator = () => Animated.parallel([
    animation(happyScoreAnimatedValue, tileData[selectedTileIndex]?.score ?? 0, 1000),
    animation(
      happyScoreComparedAnimatedValue,
      tileData[selectedTileIndex]?.scoreCompared ?? 0,
      1000,
    ),
  ]).start();

  useEffect(() => {
    getClientDetails().then((response) => {
      Object.keys(response?.clientDetails?.happy_score_insights ?? []).forEach((item) => {
        const id = item.split('_')[0];
        const newTileData = tileData;
        if (id.includes('happy')) {
          newTileData[0].description = response.clientDetails.happy_score_insights[item].text;
        } else if (id.includes('internal')) {
          newTileData[1].description = response.clientDetails.happy_score_insights[item].text;
        } else if (id.includes('external')) {
          newTileData[2].description = response.clientDetails.happy_score_insights[item].text;
        } else if (id === 'it') {
          newTileData[3].description = response.clientDetails.happy_score_insights[item].text;
        } else if (id.includes('energy')) {
          newTileData[4].description = response.clientDetails.happy_score_insights[item].text;
        } else if (id.includes('building')) {
          newTileData[5].description = response.clientDetails.happy_score_insights[item].text;
        } else if (id.includes('amen')) {
          newTileData[6].description = response.clientDetails.happy_score_insights[item].text;
        } else if (id.includes('ergo')) {
          newTileData[7].description = response.clientDetails.happy_score_insights[item].text;
        } else if (id.includes('occupant')) {
          newTileData[8].description = response.clientDetails.happy_score_insights[item].text;
        }
        setTileData(newTileData);
      });
    });

    happyScoreMetrics.forEach(({ metric }, metricIndex: number) => {
      GetMetricHappyScoreData({
        metric,
        range: {
          start: Moment().subtract(1, 'day').startOf('day').toDate(),
          end: Moment().subtract(1, 'day').endOf('day').toDate(),
        },
      }).then((data) => {
        tileData[metricIndex].yesterdayScore = getAverageFromGraphData(data);
      });
      GetMetricHappyScoreData({
        metric,
        range: {
          start: Moment().startOf('day').toDate(),
          end: Moment().endOf('day').toDate(),
        },
      }).then((data) => {
        tileData[metricIndex].score = data?.[data.length - 1]?.value;
        tileData[metricIndex].todayScore = getAverageFromGraphData(data);
      });
    });
  }, []);

  const updateGraphData = (compare: boolean = false) => {
    const { start, end } = compare ? compareRange : range;
    const newGraphData: object[][] = [[], [], [], [], [], [], [], [], []];
    if (compare) setGraphCompareData(newGraphData);
    else setGraphData(newGraphData);
    if (!start || !end) return;
    const newTileData = [...tileData];
    happyScoreMetrics.forEach(({ metric }, metricIndex: number) => {
      GetMetricHappyScoreData({ metric, range: { start, end } })?.then((data) => {
        if (!data?.length) return;
        newTileData[metricIndex][compare
          ? 'scoreCompared' : 'average'] = getAverageFromGraphData(data);
        setTileData(newTileData);
        animateHappyScoreIndicator();
        newGraphData[metricIndex] = [...data
          .map(({ value, date }, index: number) => ({ value, date, index }))];
        if (compare) setGraphCompareData([...newGraphData]);
        else setGraphData([...newGraphData]);
      });
    });
  };

  useEffect(() => updateGraphData(false), [range]);
  useEffect(() => updateGraphData(true), [compareRange]);

  useEffect(animateHappyScoreIndicator, [selectedTileIndex]);

  useEffect(() => {
    const topSectionHeightAnimation: Animated.CompositeAnimation = animation(
      topSectionHeight,
      topSectionDefaultShown ? 327 : 71,
    );
    const happyScoreIndicatorSizeAnimation: Animated.CompositeAnimation = animation(
      happyScoreIndicatorSize,
      topSectionDefaultShown ? 1 : 0.59,
    );
    const happyScoreIndicatorOffsetAnimation: Animated.CompositeAnimation = animation(
      happyScoreIndicatorOffset,
      topSectionDefaultShown ? -60 : -80,
    );
    const topSectionMarginStartAnimation: Animated.CompositeAnimation = animation(
      topSectionMarginStart,
      topSectionDefaultShown
        // ? 500 * Math.min(1, (Dimensions.get('window').width - 252) / 1188)
        ? 100 : 82,
    );
    const sectionHeaderPaddingStartAnimation: Animated.CompositeAnimation = animation(
      sectionHeaderPaddingStart,
      topSectionDefaultShown ? 172 : 102,
    );
    const graphContainerHeightAnimation: Animated.CompositeAnimation = animation(
      graphContainerHeight,
      // eslint-disable-next-line no-nested-ternary
      topSectionDefaultShown ? 0 : selectedTileIndex === 0 ? 445 : 380,
    );
    const graphContainerMarginTopAnimation: Animated.CompositeAnimation = animation(
      graphContainerMarginTop,
      topSectionDefaultShown ? 0 : 16,
    );
    const graphContainerOpacityAnimation: Animated.CompositeAnimation = animation(
      graphContainerOpacity,
      topSectionDefaultShown ? 0 : 1,
    );
    Animated.parallel([
      topSectionHeightAnimation,
      happyScoreIndicatorSizeAnimation,
      happyScoreIndicatorOffsetAnimation,
      topSectionMarginStartAnimation,
      sectionHeaderPaddingStartAnimation,
      graphContainerHeightAnimation,
      graphContainerMarginTopAnimation,
      graphContainerOpacityAnimation,
    ]).start();
    const timeout = setTimeout(() => {
      setDefaultContentShown(topSectionDefaultShown);
      clearTimeout(timeout);
    }, defaultAnimationDuration);
  }, [topSectionDefaultShown, selectedTileIndex]);

  const navigation = useNavigation();

  return (
    <View style={{ maxWidth: screenMaxWidth, zIndex: 2 }}>
      <View style={[
        styles.contentWrapper,
        !defaultContentShown && styles.superImposition,
        { maxWidth: screenMaxWidth },
      ]}>
        <Animated.View
          style={[
            styles.contentContainer,
            { height: topSectionHeight, marginStart: topSectionMarginStart },
          ]}
        >
          <SectionHeader
            defaultShown={topSectionDefaultShown}
            setDefaultShown={setTopSectionDefaultShown}
            title1={selectedTileIndex === 0 ? 'Happy score'
              : HappyScoreCategoryTitles[tileData[selectedTileIndex].screen]}
            button1="INSIGHTS"
            button2="GRAPH"
            showLinkArrow={selectedTileIndex !== 0}
            linkAction={() => selectedTileIndex > 0
              && navigation.navigate(linkedScreens[selectedTileIndex - 1] as any)
            }
            style={[styles.sectionHeader, { paddingStart: sectionHeaderPaddingStart }]}
          />
          {defaultContentShown && topSectionDefaultShown && (
            <TopSectionInsights
              {...tileData[selectedTileIndex]}
              buttonShown
            />
          )}
          <HappyScoreIndicator
            style={[
              styles.happyScoreIndicator,
              {
                transform: [{ scale: happyScoreIndicatorSize }],
                top: happyScoreIndicatorOffset,
                start: happyScoreIndicatorOffset,
              },
            ]}
            showComparison={!!compareRange.start && !!compareRange.end}
            gradientStartColor="#2BC3A5"
            gradientEndColor="#85E1CF"
            textColor={Colors.light.hereworksExtraDarkTeal}
            happyScoreAnimatedValue={happyScoreAnimatedValue}
            happyScoreComparedAnimatedValue={happyScoreComparedAnimatedValue}
            noValue={!Number.isFinite(tileData[selectedTileIndex]?.score)}
            noValueCompared={!Number.isFinite(tileData[selectedTileIndex]?.scoreCompared)}
          />
        </Animated.View>
        <Animated.View
          style={[
            styles.graphContainer,
            {
              height: graphContainerHeight,
              marginTop: graphContainerMarginTop,
              opacity: graphContainerOpacity,
            },
          ]}
        >
          <View style={styles.dateComponentContainer}>
            <DateComponent
              selectedRange={range.selectedRange}
              compareEnabled
              onChange={(start, end, selectedRange) => setRange({ start, end, selectedRange })}
              onCompareChange={(start, end, selectedRange) => setCompareRange(
                { start, end, selectedRange },
              )}
              onOpen={() => { }}
              onConfirm={() => { }}
              startDate={range.start}
              endDate={range.end}
              compareStartDate={compareRange.start}
              compareEndDate={compareRange.end}
            />
          </View>
          {selectedTileIndex === 0 && <HomeGraphLineSelector
            data={tileData}
            setData={setTileData}
            showComparison={!!compareRange.start && !!compareRange.end}
          />}
          {!defaultContentShown && <HappyDetailsGraphWeb
            {...{
              tileData, graphCompareData, graphData, selectedTileIndex,
            }}
            linesShown={tileData.map((tile: { lineShown: boolean }) => tile.lineShown)}
            showComparison={!!compareRange.end}
          />}
        </Animated.View>
      </View>
      <TileSelector
        data={tileData}
        selectedIndex={selectedTileIndex}
        setSelectedIndex={(index) => {
          setSelectedTileIndex(index);
          props.setSelectedIndex(index);
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  contentWrapper: {
    marginTop: 104,
    marginEnd: 40,
  },
  superImposition: {
    zIndex: 2,
  },
  contentContainer: {
    height: 327,
    backgroundColor: '#FFF',
    borderRadius: 24,
    paddingTop: 17,
    paddingBottom: 27,
  },
  sectionHeader: {
    paddingStart: 172,
  },
  graphContainer: {
    marginStart: 40,
    borderRadius: 24,
    paddingStart: 24,
    backgroundColor: '#FFF',
    borderColor: Colors.light.hereworksWhite300,
    borderWidth: 1,
  },
  happyScoreIndicator: {
    top: -60,
    start: -60,
  },
  dateComponentContainer: {
    marginTop: 18,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 2,
  },
});

export default HomeScreenTopSection;
